import * as THREE from 'three';
import type { QPose } from '../animation/pose';
import { merge, part, shade, sharedMaterials } from './geometry';

export const WOLF_JOINTS = [
  'body',
  'chest',
  'neck',
  'head',
  'jaw',
  'tail1',
  'tail2',
  'legFL',
  'kneeFL',
  'legFR',
  'kneeFR',
  'legBL',
  'kneeBL',
  'legBR',
  'kneeBR',
] as const;
export type WolfJoint = (typeof WOLF_JOINTS)[number];

/** Procedural quadruped rig (Grey Meadow Wolf). Faces +Z, stands on y=0, ~0.85 m at the shoulder. */
export class WolfRig {
  readonly root = new THREE.Group();
  readonly bodyGroup = new THREE.Group();
  readonly joints = {} as Record<WolfJoint, THREE.Object3D>;
  private readonly meshes: THREE.Mesh[] = [];
  readonly height = 1.0;

  constructor(color: string, accent: string, scale = 1) {
    this.root.add(this.bodyGroup);
    this.bodyGroup.scale.setScalar(scale);
    const dark = shade(color, -0.12);
    const light = shade(color, 0.18);
    const mk = (name: WolfJoint, parent: THREE.Object3D, pos: [number, number, number]): THREE.Object3D => {
      const o = new THREE.Object3D();
      o.position.set(...pos);
      parent.add(o);
      this.joints[name] = o;
      return o;
    };
    const attach = (j: THREE.Object3D, geos: THREE.BufferGeometry[]): void => {
      const m = new THREE.Mesh(merge(geos), sharedMaterials.cloth);
      m.castShadow = true;
      j.add(m);
      this.meshes.push(m);
    };
    const body = mk('body', this.bodyGroup, [0, 0.66, -0.2]);
    const chest = mk('chest', body, [0, 0.04, 0.45]);
    const neck = mk('neck', chest, [0, 0.12, 0.2]);
    const head = mk('head', neck, [0, 0.12, 0.14]);
    const jaw = mk('jaw', head, [0, -0.06, 0.08]);
    const tail1 = mk('tail1', body, [0, 0.06, -0.38]);
    const tail2 = mk('tail2', tail1, [0, 0, -0.25]);
    attach(body, [
      part.sphere(0.24, color, { pos: [0, 0, -0.05], scale: [0.9, 0.85, 1.45] }),
      part.sphere(0.2, dark, { pos: [0, 0.08, -0.05], scale: [0.8, 0.5, 1.4] }),
      part.sphere(0.17, light, { pos: [0, -0.1, 0.0], scale: [0.85, 0.5, 1.2] }),
    ]);
    attach(chest, [
      part.sphere(0.25, color, { pos: [0, 0, 0], scale: [0.95, 1.0, 1.0] }),
      part.sphere(0.2, light, { pos: [0, -0.1, 0.12], scale: [0.8, 0.8, 0.7] }),
      part.sphere(0.21, dark, { pos: [0, 0.1, -0.02], scale: [0.95, 0.6, 1.0] }),
    ]);
    attach(neck, [part.cyl(0.11, 0.15, 0.28, color, { pos: [0, 0.02, 0.02], rot: [-0.9, 0, 0] })]);
    attach(head, [
      part.sphere(0.13, color, { pos: [0, 0.02, 0], scale: [1, 0.9, 1.1] }),
      part.cyl(0.04, 0.075, 0.22, light, { pos: [0, -0.02, 0.17], rot: [Math.PI / 2, 0, 0] }, 8),
      part.sphere(0.025, '#1a1614', { pos: [0, 0.0, 0.29] }),
      part.cone(0.05, 0.13, dark, { pos: [0.07, 0.15, -0.03], rot: [-0.2, 0, -0.25] }, 4),
      part.cone(0.05, 0.13, dark, { pos: [-0.07, 0.15, -0.03], rot: [-0.2, 0, 0.25] }, 4),
      part.sphere(0.018, '#e8c34a', { pos: [0.06, 0.05, 0.12] }),
      part.sphere(0.018, '#e8c34a', { pos: [-0.06, 0.05, 0.12] }),
    ]);
    attach(jaw, [part.box(0.08, 0.035, 0.16, shade(accent, 0.05), { pos: [0, -0.01, 0.1] })]);
    attach(tail1, [
      part.cyl(0.07, 0.05, 0.28, color, { pos: [0, 0, -0.12], rot: [Math.PI / 2 + 0.5, 0, 0] }),
    ]);
    attach(tail2, [
      part.cone(0.06, 0.32, light, { pos: [0, -0.05, -0.12], rot: [-Math.PI / 2 - 0.3, 0, 0] }, 8),
    ]);
    const legs: [WolfJoint, WolfJoint, THREE.Object3D, [number, number, number]][] = [
      ['legFL', 'kneeFL', chest, [0.13, -0.08, 0.04]],
      ['legFR', 'kneeFR', chest, [-0.13, -0.08, 0.04]],
      ['legBL', 'kneeBL', body, [0.13, -0.05, -0.28]],
      ['legBR', 'kneeBR', body, [-0.13, -0.05, -0.28]],
    ];
    for (const [legName, kneeName, parent, pos] of legs) {
      const leg = mk(legName, parent, pos);
      const knee = mk(kneeName, leg, [0, -0.28, 0]);
      const back = legName.startsWith('legB');
      attach(leg, [part.cyl(back ? 0.075 : 0.06, 0.045, 0.3, color, { pos: [0, -0.13, back ? -0.02 : 0] })]);
      attach(knee, [
        part.cyl(0.04, 0.032, 0.3, dark, { pos: [0, -0.15, 0] }),
        part.box(0.07, 0.04, 0.1, dark, { pos: [0, -0.3, 0.03] }),
      ]);
    }
  }

  applyPose(q: QPose): void {
    for (const [name, quat] of q.joints) {
      const j = this.joints[name as WolfJoint];
      if (j) j.quaternion.copy(quat);
    }
    const r = q.root;
    this.bodyGroup.position.set(r.rootX, r.rootY, r.rootZ);
    this.bodyGroup.rotation.set(r.rootPitch, r.rootYaw, r.rootRoll, 'YXZ');
  }

  dispose(): void {
    for (const m of this.meshes) m.geometry.dispose();
  }
}
